import { IOperationResultNoValue } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import {
	AccountDetailsContext,
	AccountDetailsSettingsContext,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import {
	IDonationDataboardTagSettings,
	IServiceJobDataboardTagSettings,
} from '../../../../../viewmodels/AppViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Input } from '@HoudiniUI/Input';
import { KeyDateIcon } from '../../../../../web/components/svgs/icons/KeyDateIcon';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { WarningIconSmall, WarningIconType } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { DeleteKeyFactsModal } from '../../../../components/DeleteKeyFactsModal';
import { RestoreKeyFactsModal } from '../../../../components/RestoreKeyFactsModal';
import { TagSettingsModal } from './TagSettingsModal';
import { ArchiveAllDonationsConfirmationOptions } from './options';
import { AccountSettingsSections } from './AccountSettingsSections';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	scrollToBottomPortalId?: string;
	styles?: StyleDeclarationValue[];
}

const SettingsFc: React.FC<IProps> = () => {
	const [settingsSearch, setSettingsSearch] = useState('');
	const [showRestoreKeyFactsModal, setShowRestoreKeyFactsModal] = useState(false);
	const [showDeleteKeyFactsModal, setShowDeleteKeyFactsModal] = useState(false);
	const adminUserSession = useUserSession<AdminUserSessionContext>();

	const accountDetailsContext = React.useContext(AccountDetailsContext);

	const canModifyAccount = !!adminUserSession.hasPermission(AdminPermission.ModifyAccount);
	const hasDevPermission = !!adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasSupportPermission = !!adminUserSession.hasPermission(AdminPermission.SupportLevel3);
	// The gate the Permanent column carried as <ConditionalAccess>, needed as a value so the
	// group cards, pills and no-match chips all agree on whether that group exists.
	const canSeePermanentSettings =
		hasDevPermission || hasSupportPermission || !!adminUserSession.hasPermission(AdminPermission.ResetAccount);

	const errorMessages = useErrorMessages();

	const deleteAllDonationsModal = useModal(
		false,

		(result: IConfirmationDialogOption<boolean>) => {
			if (result?.isDestructive) {
				accountDetailsContext.accountOperations
					.deleteAllDonations()
					?.then(() => {
						toast.success('All donations archived successfully');
					})
					?.catch((error: IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});
			}
		},
		[accountDetailsContext.accountOperations]
	);

	const settingsContext = React.useContext(AccountDetailsSettingsContext);

	const [donationTagModalOpen, setDonationTagModalOpen] = useState(false);
	const [pendingDonationTagSettings, setPendingDonationTagSettings] = useState<
		Omit<IDonationDataboardTagSettings, '_type'>
	>({
		enabled: true,
		lastDonationTagsEnabled: true,
		lifetimeDonationTagsEnabled: true,
		yearlyDonationTagsEnabled: true,
	});

	const openDonationTagModal = () => {
		const tagSettings = accountDetailsContext.accountOperations.account?.features?.boards?.donationBoard?.configuration
			?.tagSettings as IDonationDataboardTagSettings | undefined;
		setPendingDonationTagSettings({
			enabled: tagSettings?.enabled ?? true,
			lastDonationTagsEnabled: tagSettings?.lastDonationTagsEnabled ?? true,
			lifetimeDonationTagsEnabled: tagSettings?.lifetimeDonationTagsEnabled ?? true,
			yearlyDonationTagsEnabled: tagSettings?.yearlyDonationTagsEnabled ?? true,
		});
		setDonationTagModalOpen(true);
	};

	const closeDonationTagModal = () => {
		setDonationTagModalOpen(false);
	};

	const saveDonationTagSettings = () => {
		settingsContext.onDonationTagSettingsChanged?.(pendingDonationTagSettings);
		setDonationTagModalOpen(false);
	};

	const [serviceJobTagModalOpen, setServiceJobTagModalOpen] = useState(false);
	const [pendingServiceJobTagSettings, setPendingServiceJobTagSettings] = useState<
		Omit<IServiceJobDataboardTagSettings, '_type'>
	>({
		enabled: false,
		jobStatusTagEnabled: true,
		lastJobTypeTagEnabled: true,
		lastJobDateTagEnabled: true,
		jobInsightTagsEnabled: true,
	});

	const openServiceJobTagModal = () => {
		const tagSettings = accountDetailsContext.accountOperations.account?.features?.boards?.serviceJobBoard
			?.configuration?.tagSettings as IServiceJobDataboardTagSettings | undefined;
		setPendingServiceJobTagSettings({
			// Opt-in, unlike the donation board: ServiceJobBoardTagSettings.Enabled defaults to false server-side,
			// so defaulting this to true would show tagging on for an account the API treats as having it off.
			enabled: tagSettings?.enabled ?? false,
			jobStatusTagEnabled: tagSettings?.jobStatusTagEnabled ?? true,
			lastJobTypeTagEnabled: tagSettings?.lastJobTypeTagEnabled ?? true,
			lastJobDateTagEnabled: tagSettings?.lastJobDateTagEnabled ?? true,
			jobInsightTagsEnabled: tagSettings?.jobInsightTagsEnabled ?? true,
		});
		setServiceJobTagModalOpen(true);
	};

	const closeServiceJobTagModal = () => {
		setServiceJobTagModalOpen(false);
	};

	const saveServiceJobTagSettings = () => {
		settingsContext.onServiceJobTagSettingsChanged?.(pendingServiceJobTagSettings);
		setServiceJobTagModalOpen(false);
	};

	const onClearSettingsSearch = () => {
		setSettingsSearch('');
	};

	const onSettingsSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSettingsSearch(e.target.value);
	};
	const onRestoreKeyFacts = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowRestoreKeyFactsModal(true);
	};

	const onRestoreKeyFactsRequestClose = () => {
		setShowRestoreKeyFactsModal(false);
	};

	const onDeleteKeyFacts = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowDeleteKeyFactsModal(true);
	};

	const onDeleteKeyFactsRequestClose = () => {
		setShowDeleteKeyFactsModal(false);
	};

	return (
		<>
			{accountDetailsContext.accountOperations.account ? (
				<>
					<ConditionalAccess permission={AdminPermission.ModifyAccount}>
						<div className={css(styleSheet.deleteKeyFactsHeader)}>
							<div className={css(styleSheet.settingsSearchContainer)}>
								<Input
									styles={[styleSheet.settingsSearch]}
									id='admin-settings-search'
									onChange={onSettingsSearch}
									placeholder='Search Settings'
									type='text'
									value={settingsSearch}
								/>
								{settingsSearch.length > 0 ? (
									<button className={css(baseStyleSheet.brandLink)} onClick={onClearSettingsSearch}>
										Clear
									</button>
								) : null}
							</div>
							<div className={css(styleSheet.buttonsWrapper)}>
								<div>
									<button className={`${css(styleSheet.actionEntities)}`} onClick={onRestoreKeyFacts}>
										<KeyDateIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Restore Key Facts</span>
									</button>

									<RestoreKeyFactsModal
										userSession={adminUserSession}
										modalProps={{
											isOpen: showRestoreKeyFactsModal,
											onRequestClose: onRestoreKeyFactsRequestClose,
										}}
									/>
									<button className={`${css(styleSheet.actionEntities)}`} onClick={onDeleteKeyFacts}>
										<TrashIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Delete Key Facts</span>
									</button>
									<DeleteKeyFactsModal
										userSession={adminUserSession}
										modalProps={{
											isOpen: showDeleteKeyFactsModal,
											onRequestClose: onDeleteKeyFactsRequestClose,
										}}
									/>
								</div>
							</div>
						</div>
					</ConditionalAccess>
					<AccountSettingsSections
						canModifyAccount={canModifyAccount}
						hasDevPermission={hasDevPermission}
						hasSupportPermission={hasSupportPermission}
						canSeePermanentSettings={canSeePermanentSettings}
						settingsSearch={settingsSearch}
						onClearSettingsSearch={onClearSettingsSearch}
						onClearAllDonations={hasSupportPermission ? deleteAllDonationsModal.setIsOpen(true) : undefined}
						onOpenDonationTagSettings={openDonationTagModal}
						onOpenServiceJobTagSettings={openServiceJobTagModal}
					/>
					<ConditionalAccess permission={AdminPermission.SupportLevel3}>
						<ConfirmationDialog
							className={css(styleSheet.deleteRenewKeyFactsModal)}
							icon={<WarningIconSmall type={WarningIconType.Red} />}
							modalProps={{
								isOpen: deleteAllDonationsModal.isOpen,
								onRequestClose: deleteAllDonationsModal.onRequestClose,
							}}
							options={ArchiveAllDonationsConfirmationOptions}
							title='Archive All Donations?'
						>
							<div>
								<p>Are you sure you want to archive all donations for this account?</p>
							</div>
						</ConfirmationDialog>
						{accountDetailsContext.accountOperations.isDeletingAllDonations && (
							<LoadingSpinner className={css(styleSheet.loading)} type='large' />
						)}
					</ConditionalAccess>
					<TagSettingsModal
						isOpen={donationTagModalOpen}
						title='Donation Tag Settings'
						master={{
							id: 'donation-tagging-enabled',
							label: 'Donation Tagging',
							isOn: pendingDonationTagSettings.enabled,
							onChange: checked => setPendingDonationTagSettings(prev => ({ ...prev, enabled: checked })),
							disabledWarning: 'Disabling will remove all existing donation tags from contacts.',
						}}
						rows={[
							{
								id: 'last-donation-tags',
								label: 'Last Donation Tags',
								hoverText: 'Creates "Last Donation: <amount>" tags',
								isOn: pendingDonationTagSettings.lastDonationTagsEnabled,
								onChange: checked =>
									setPendingDonationTagSettings(prev => ({ ...prev, lastDonationTagsEnabled: checked })),
							},
							{
								id: 'lifetime-donation-tags',
								label: 'Lifetime Donation Tags',
								hoverText: 'Creates "Lifetime Donations: <amount>" tags',
								isOn: pendingDonationTagSettings.lifetimeDonationTagsEnabled,
								onChange: checked =>
									setPendingDonationTagSettings(prev => ({ ...prev, lifetimeDonationTagsEnabled: checked })),
							},
							{
								id: 'yearly-donation-tags',
								label: 'Yearly Donation Tags',
								hoverText: 'Creates "YYYY Donations: <amount>" tags',
								isOn: pendingDonationTagSettings.yearlyDonationTagsEnabled,
								onChange: checked =>
									setPendingDonationTagSettings(prev => ({ ...prev, yearlyDonationTagsEnabled: checked })),
							},
						]}
						infoText='Disabling a tag type will remove existing tags of that type on the next donation update.'
						onSave={saveDonationTagSettings}
						onCancel={closeDonationTagModal}
					/>
					<TagSettingsModal
						isOpen={serviceJobTagModalOpen}
						title='Service Job Tag Settings'
						master={{
							id: 'service-job-tagging-enabled',
							label: 'Service Job Tagging',
							isOn: pendingServiceJobTagSettings.enabled,
							onChange: checked => setPendingServiceJobTagSettings(prev => ({ ...prev, enabled: checked })),
							disabledWarning: 'Disabling will remove all existing service job tags from contacts.',
						}}
						rows={[
							{
								id: 'job-status-tags',
								label: 'Job Status Tags',
								hoverText: 'Tags contacts with the status of each of their jobs',
								isOn: pendingServiceJobTagSettings.jobStatusTagEnabled,
								onChange: checked =>
									setPendingServiceJobTagSettings(prev => ({ ...prev, jobStatusTagEnabled: checked })),
							},
							{
								id: 'last-job-type-tag',
								label: 'Last Job Type Tag',
								hoverText: 'Creates a "Last Job Type: <type>" tag, e.g. "Last Job Type: Install"',
								isOn: pendingServiceJobTagSettings.lastJobTypeTagEnabled,
								onChange: checked =>
									setPendingServiceJobTagSettings(prev => ({ ...prev, lastJobTypeTagEnabled: checked })),
							},
							{
								id: 'last-job-date-tag',
								label: 'Last Job Date Tag',
								hoverText: 'Creates a "Last Job: <month year>" tag, e.g. "Last Job: June 2026"',
								isOn: pendingServiceJobTagSettings.lastJobDateTagEnabled,
								onChange: checked =>
									setPendingServiceJobTagSettings(prev => ({ ...prev, lastJobDateTagEnabled: checked })),
							},
							{
								id: 'job-insight-tags',
								label: 'Revenue & History Tags',
								hoverText:
									'Creates "Lifetime Revenue", "<year> Revenue", "Largest Job", "Job Count", "Serviced In: <year>", "Job Type" and "Has Scheduled Job" tags',
								isOn: pendingServiceJobTagSettings.jobInsightTagsEnabled,
								onChange: checked =>
									setPendingServiceJobTagSettings(prev => ({ ...prev, jobInsightTagsEnabled: checked })),
							},
						]}
						infoText='The last job tags describe the most recently completed job, and follow it as newer jobs complete.'
						onSave={saveServiceJobTagSettings}
						onCancel={closeServiceJobTagModal}
					/>
				</>
			) : (
				<LoadingSpinner className={css(styleSheet.loading)} type='large' />
			)}
		</>
	);
};

const SettingsWithObserver = observer(SettingsFc);
export const Settings = withRouter(SettingsWithObserver);
